import React, { useState, useRef, useEffect } from 'react';
import './Contact.css';

const subjectOptions = [
  { value: '', label: 'Select a topic...' },
  { value: 'training', label: 'Training & Certifications' },
  { value: 'staffing', label: 'Staff Augmentation' },
  { value: 'it-services', label: 'IT Services' },
  { value: 'careers', label: 'Career Opportunities' },
  { value: 'general', label: 'General Inquiry' },
];

function CustomSelect({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const selected = subjectOptions.find(o => o.value === value) || subjectOptions[0];

  return (
    <div className={`custom-select${open ? ' open' : ''}`} ref={ref}>
      <button type="button" className="custom-select-trigger" onClick={() => setOpen(v => !v)}>
        <span className={value ? '' : 'placeholder'}>{selected.label}</span>
        <svg className="custom-select-arrow" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="6 9 12 15 18 9"/></svg>
      </button>
      {open && (
        <ul className="custom-select-list">
          {subjectOptions.map(opt => (
            <li
              key={opt.value}
              className={`custom-select-option${opt.value === value ? ' selected' : ''}${opt.value === '' ? ' placeholder-opt' : ''}`}
              onMouseDown={() => { onChange(opt.value); setOpen(false); }}
            >
              {opt.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

const IconGradCap = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 10v6M2 10l10-5 10 5-10 5z"/>
    <path d="M6 12v5c3.33 1.67 8.67 1.67 12 0v-5"/>
  </svg>
);
const IconUsers2 = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/>
    <circle cx="9" cy="7" r="4"/>
    <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"/>
  </svg>
);
const IconShield = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
  </svg>
);
const IconBriefcase = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="7" width="20" height="14" rx="2"/>
    <path d="M16 7V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v2"/>
    <line x1="12" y1="12" x2="12" y2="12"/>
    <path d="M2 12h20"/>
  </svg>
);

export default function Contact() {
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '', subscribe: false });
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'Name is required';
    if (!form.email.trim()) e.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'Enter a valid email';
    if (!form.message.trim()) e.message = 'Message is required';
    return e;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    setErrors({});
    setSubmitted(true);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  return (
    <div className="contact-page">
      {/* Hero */}
      <section className="page-hero">
        <div className="container page-hero-content">
          <span className="section-label section-label--blue">Get In Touch</span>
          <h1>Let Us Start a <span style={{ color: '#d5772d' }}>Conversation</span></h1>
          <p>Whether you need training, staffing solutions, or IT services - we would love to hear about your challenges and how we can help.</p>
        </div>
      </section>

      <section className="contact-section">
        <div className="container">
          <div className="contact-grid">

            {/* Info Panel */}
            <div className="contact-info">
              <h2>How Can We Help?</h2>
              <p>Tell us about your cybersecurity needs and one of our experts will get back to you within one business day.</p>

              <div className="contact-options">
                {[
                  { icon: <IconGradCap />, title: 'Training Inquiries', desc: 'Ask about course schedules, custom training programs, and group pricing.' },
                  { icon: <IconUsers2 />, title: 'Staff Augmentation', desc: "Discuss your talent requirements and we will match you with pre-vetted professionals." },
                  { icon: <IconShield />, title: 'IT & Security Services', desc: 'Get a consultation on your security posture, cloud needs, or IT infrastructure.' },
                  { icon: <IconBriefcase />, title: 'Career Opportunities', desc: "Interested in joining our team? Send your resume and we will be in touch." },
                ].map((opt, i) => (
                  <div className="contact-option" key={i}>
                    <div className="contact-opt-icon">{opt.icon}</div>
                    <div>
                      <h4>{opt.title}</h4>
                      <p>{opt.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="contact-socials">
                <p className="socials-label">Connect with us</p>
                <div className="socials-row">
                  {[
                    { label: 'Facebook', icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M18 2h-3a5 5 0 00-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 011-1h3z"/></svg> },
                    { label: 'Twitter', icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M23 3a10.9 10.9 0 01-3.14 1.53 4.48 4.48 0 00-7.86 3v1A10.66 10.66 0 013 4s-4 9 5 13a11.64 11.64 0 01-7 2c9 5 20 0 20-11.5a4.5 4.5 0 00-.08-.83A7.72 7.72 0 0023 3z"/></svg> },
                    { label: 'Instagram', icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="2" width="20" height="20" rx="5"/><circle cx="12" cy="12" r="5"/><circle cx="17.5" cy="6.5" r="1.5" fill="currentColor" stroke="none"/></svg> },
                    { label: 'LinkedIn', icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M16 8a6 6 0 016 6v7h-4v-7a2 2 0 00-2-2 2 2 0 00-2 2v7h-4v-7a6 6 0 016-6zM2 9h4v12H2z"/><circle cx="4" cy="4" r="2"/></svg> },
                  ].map(s => (
                    <a key={s.label} href="#!" className="contact-social" aria-label={s.label}>{s.icon}</a>
                  ))}
                </div>
              </div>
            </div>

            {/* Form */}
            <div className="contact-form-wrap">
              {submitted ? (
                <div className="form-success">
                  <div className="success-icon">
                    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                  </div>
                  <h3>Message Sent!</h3>
                  <p>Thank you for reaching out. A member of our team will get back to you within one business day.</p>
                  <button className="btn-primary" onClick={() => { setSubmitted(false); setForm({ name: '', email: '', subject: '', message: '', subscribe: false }); }} style={{ marginTop: '24px' }}>
                    Send Another Message
                  </button>
                </div>
              ) : (
                <form className="contact-form" onSubmit={handleSubmit} noValidate>
                  <h3 className="form-title">Send Us a Message</h3>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="name">Full Name <span className="req">*</span></label>
                      <input
                        id="name" name="name" type="text"
                        placeholder="John Smith"
                        value={form.name} onChange={handleChange}
                        className={errors.name ? 'error' : ''}
                      />
                      {errors.name && <span className="form-error">{errors.name}</span>}
                    </div>
                    <div className="form-group">
                      <label htmlFor="email">Email Address <span className="req">*</span></label>
                      <input
                        id="email" name="email" type="email"
                        placeholder="john@company.com"
                        value={form.email} onChange={handleChange}
                        className={errors.email ? 'error' : ''}
                      />
                      {errors.email && <span className="form-error">{errors.email}</span>}
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Subject</label>
                    <CustomSelect
                      value={form.subject}
                      onChange={(val) => setForm(prev => ({ ...prev, subject: val }))}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="message">Message <span className="req">*</span></label>
                    <textarea
                      id="message" name="message"
                      placeholder="Tell us about your needs, challenges, or questions..."
                      rows={6}
                      value={form.message} onChange={handleChange}
                      className={errors.message ? 'error' : ''}
                    />
                    {errors.message && <span className="form-error">{errors.message}</span>}
                  </div>

                  <div className="form-group">
                    <label htmlFor="file" className="file-label">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                      Attach a File (optional)
                    </label>
                    <input id="file" type="file" accept=".jpg,.png,.pdf,.doc,.docx,.gif" className="file-input" />
                    <p className="file-hint">Supported: JPG, PNG, PDF, DOC, DOCX, GIF · Max 10 MB</p>
                  </div>

                  <div className="form-checkbox">
                    <input
                      type="checkbox" id="subscribe" name="subscribe"
                      checked={form.subscribe} onChange={handleChange}
                    />
                    <label htmlFor="subscribe">
                      Sign me up for the ThirdEye newsletter - updates, insights, and promotions.
                    </label>
                  </div>

                  <button type="submit" className="btn-primary form-submit">
                    Send Message
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
